import { DeliveryChannel } from '@pushly/models/lib/enums/delivery-channels.enum'
import { Select, Well } from '@pushly/aqe/lib/components'
import React, { FC } from 'react'
import { ReactNode, useState } from 'react'
import { InfoCircleOutlined, RotateLeftOutlined } from '@ant-design/icons'
import { getClassNames } from '../../../_utils/classnames'
import { AppMessageUiTemplate } from '@pushly/models/lib/structs/app-messages/app-message-ui-template'
import PreviewSDK from '@pushly/cuttlefish'
import classNames from 'classnames'
import { Button, Flex, Modal, Popover, Space, theme as AntTheme } from 'antd'
import { useThemeMode } from '@pushly/aqe'

type AppMessagePreviewProps = {
    template: AppMessageUiTemplate
    hideHeader?: boolean
    loading?: boolean | ReactNode
}

type PreviewDeviceType = 'phone' | 'tablet'
type PreviewOrientation = 'portrait' | 'landscape'
type SystemMode = 'light' | 'dark'

type PreviewPlatformSelectProps = {
    value: DeliveryChannel
    onChange: (channel: DeliveryChannel) => void
    options: { value: DeliveryChannel; label: string }[]
}

type PreviewSystemModeSelectProps = {
    value: 'light' | 'dark'
    onChange: (mode: 'light' | 'dark') => void
}

type PreviewDeviceSelectProps = {
    value: PreviewDeviceType
    onChange: (deviceType: PreviewDeviceType) => void
}

type PreviewOrientationSelectProps = {
    value: PreviewOrientation
    onChange: (orientation: PreviewOrientation) => void
}

const PreviewPlatformSelect: FC<PreviewPlatformSelectProps> = ({ value, onChange, options }) => {
    return (
        <Select
            className="platform-select"
            prefix="Platform:"
            aria-label="Preview platform"
            style={{ width: 220 }}
            showSearch={false}
            onChange={(val: DeliveryChannel) => {
                onChange(val)
            }}
            value={[value]}
            options={options}
            disabled={options.length === 1}
        />
    )
}

const PreviewSystemModeSelect: FC<PreviewSystemModeSelectProps> = ({ value, onChange }) => {
    return (
        <Select
            className="theme-select"
            prefix="Theme:"
            aria-label="Preview theme"
            showSearch={false}
            defaultValue={['light']}
            value={[value]}
            onChange={(v: 'light' | 'dark') => {
                onChange(v)
            }}
            options={[
                {
                    value: 'light',
                    label: 'Light',
                },
                {
                    value: 'dark',
                    label: 'Dark',
                },
            ]}
        />
    )
}

const PreviewDeviceTypeSelect: FC<PreviewDeviceSelectProps> = ({ value, onChange }) => {
    return (
        <Select
            className="device-type-select"
            prefix="Device Type:"
            aria-label="Preview device type"
            showSearch={false}
            style={{ width: 220 }}
            defaultValue={['phone']}
            value={[value]}
            onChange={(v: PreviewDeviceType) => {
                onChange(v)
            }}
            options={[
                {
                    value: 'phone',
                    label: 'Phone',
                },
                {
                    value: 'tablet',
                    label: 'Tablet',
                },
            ]}
        />
    )
}

const PreviewOrientationSelect: FC<PreviewOrientationSelectProps> = ({ value, onChange }) => {
    return (
        <Select
            className="orientation-select"
            prefix="Orientation:"
            aria-label="Preview orientation"
            showSearch={false}
            style={{ width: 220 }}
            defaultValue={['portrait']}
            value={[value]}
            onChange={(v: PreviewOrientation) => {
                onChange(v)
            }}
            options={[
                {
                    value: 'portrait',
                    label: 'Portrait',
                },
                {
                    value: 'landscape',
                    label: 'Landscape',
                },
            ]}
        />
    )
}

export const AppMessagePreview = ({ template, hideHeader, loading }: AppMessagePreviewProps) => {
    const previewTemplate = template

    const sortedChannels = [...previewTemplate.channels].sort()
    const defaultChannel = sortedChannels.length === 2 ? DeliveryChannel.NATIVE_IOS : sortedChannels[0]

    const [channel, setChannel] = useState(defaultChannel)

    const { resolvedMode } = useThemeMode()
    const [theme, setTheme] = useState<SystemMode>(resolvedMode)
    const [landscapeVisible, setLandscapeVisible] = useState(false)

    const [advancedPreviewDeviceType, setAdvancedPreviewDeviceType] = useState<PreviewDeviceType>('phone')
    const [advancedPreviewOrientation, setAdvancedPreviewOrientation] = useState<PreviewOrientation>('portrait')
    const [advancedPreviewChannel, setAdvancedPreviewChannel] = useState<DeliveryChannel>(channel)
    const [advancedPreviewSystemMode, setAdvancedPreviewSystemMode] = useState<SystemMode>(theme)

    const { token } = AntTheme.useToken()

    const channelOptions = sortedChannels.map((option) => {
        return {
            value: option,
            label: option === DeliveryChannel.NATIVE_ANDROID ? 'Android' : 'Apple / iOS',
        }
    })

    return (
        <Well
            className={getClassNames('app-message-builder-preview', 'nested')}
            title={
                <span>
                    <span className="label-with-component-r">Preview</span>
                    <Popover
                        classNames={{
                            content: getClassNames('app-message-builder-popover'),
                        }}
                        content="The platform preview is a best-guess at how this app message will look across the most common devices for the selected operating system."
                    >
                        <InfoCircleOutlined className="info-icon" />
                    </Popover>
                </span>
            }
            hideHeader={hideHeader}
            hideFooter={true}
        >
            <Flex vertical gap={8} align="center">
                <Flex gap={8} justify="space-between">
                    <PreviewPlatformSelect
                        value={channel}
                        onChange={(val: DeliveryChannel) => {
                            setChannel(val)
                            setAdvancedPreviewChannel(val)
                        }}
                        options={channelOptions}
                    />

                    <PreviewSystemModeSelect
                        value={theme}
                        onChange={(v: 'light' | 'dark') => {
                            setTheme(v)
                            setAdvancedPreviewSystemMode(v)
                        }}
                    />

                    <Popover
                        content={
                            <div className="orientation-toggle-popover">
                                Preview your App Message in landscape orientation and tablet mode.
                            </div>
                        }
                        trigger={['hover']}
                        placement="topLeft"
                    >
                        <Button
                            className="orientation-toggle"
                            icon={<RotateLeftOutlined />}
                            onClick={() => {
                                setAdvancedPreviewOrientation('landscape')
                                setLandscapeVisible(true)
                            }}
                        />
                    </Popover>
                </Flex>

                <Well
                    className={classNames('app-message-portrait-preview')}
                    mode="ghost"
                    hideHeader={true}
                    hideFooter={true}
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <PreviewSDK.AppMessagePreview
                        channel={channel}
                        template={previewTemplate}
                        systemMode={theme}
                        loading={loading}
                    />
                </Well>
            </Flex>

            <Modal
                className="iam-advanced-preview-modal"
                open={landscapeVisible}
                title="App Message Previews"
                onOk={() => setLandscapeVisible(false)}
                onCancel={() => {
                    setLandscapeVisible(false)
                    setAdvancedPreviewDeviceType('phone')
                    setAdvancedPreviewOrientation('portrait')
                }}
                footer={false}
                width={1060}
                height={1000}
                styles={{
                    header: { borderBottom: `1px solid ${token.colorBorder}`, padding: '14px 0' },
                    title: { padding: '0 24px' },
                    container: { padding: 0 },
                    body: { padding: 24 },
                }}
                centered={true}
                destroyOnHidden={true}
            >
                <Flex vertical className="iam-advanced-preview-container" style={{ height: '100%' }}>
                    <Flex gap={8} className="iam-advanced-preview-header">
                        <PreviewDeviceTypeSelect
                            value={advancedPreviewDeviceType}
                            onChange={(v: PreviewDeviceType) => {
                                setAdvancedPreviewDeviceType(v)
                            }}
                        />
                        <PreviewPlatformSelect
                            value={channel}
                            onChange={(val: DeliveryChannel) => {
                                setChannel(val)
                                setAdvancedPreviewChannel(val)
                            }}
                            options={channelOptions}
                        />
                        <PreviewOrientationSelect
                            value={advancedPreviewOrientation}
                            onChange={(v: PreviewOrientation) => {
                                setAdvancedPreviewOrientation(v)
                            }}
                        />
                        <PreviewSystemModeSelect
                            value={theme}
                            onChange={(v: 'light' | 'dark') => {
                                setTheme(v)
                                setAdvancedPreviewSystemMode(v)
                            }}
                        />
                    </Flex>
                    <div className="iam-advanced-preview-content">
                        <PreviewSDK.AppMessagePreview
                            template={previewTemplate}
                            channel={advancedPreviewChannel}
                            deviceType={advancedPreviewDeviceType}
                            systemMode={advancedPreviewSystemMode}
                            orientation={advancedPreviewOrientation}
                        />
                    </div>
                </Flex>
            </Modal>
        </Well>
    )
}
